/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Models */
import { DsGroup } from '../models/ds-group';
import { BydModel } from '@sixense/byd-core-service';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';

@Injectable({
  providedIn: 'root',
})
export class DsProjectGroupsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get project user groups list.
   *
   * @param  projectId Project ID
   * @returns Observable type `<BydModel<DsGroup>>`
   */
  public getProjectIdGroups(projectId: number): Observable<BydModel<DsGroup>> {
    this.path = `/projects/${projectId}/groups`;
    return this.getModels<DsGroup>();
  }

  /**
   * Add role to a group.
   *
   * @param  projectId Project ID
   * @param  groupId Group ID
   * @param  roleId Role ID
   * @returns Observable type `<boolean>`
   */
  public postGroupRole(projectId: number, groupId: number, roleId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${groupId}/roles/${roleId}`;
    return this.post<boolean>();
  }

  /**
   * Add common role to a group.
   *
   * @param  projectId Project ID
   * @param  groupId Group ID
   * @param  roleId Role ID
   * @returns Observable type `<boolean>`
   */
  public postCommonGroupRole(projectId: number, groupId: number, roleId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${groupId}/commonroles/${roleId}`;
    return this.post<boolean>();
  }

  /**
   * Delete role from a group.
   *
   * @param  projectId Project ID
   * @param  groupId Groupe ID
   * @param  roleId Role ID
   * @returns Observable type `<boolean>`
   */
  public deleteGroupRole(projectId: number, groupId: number, roleId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${groupId}/roles/${roleId}`;
    return this.delete<boolean>();
  }

  /**
   * Delete common role from a group.
   *
   * @param  projectId Project ID
   * @param  groupId Group ID
   * @param  roleId Role ID
   * @returns Observable type `<boolean>`
   */
  public deleteCommonGroupRole(projectId: number, groupId: number, roleId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${groupId}/commonroles/${roleId}`;
    return this.delete<boolean>();
  }

  /**
   * Add user to a group.
   *
   * @param  projectId Project ID
   * @param  groupId Group ID
   * @param  userId User ID
   * @returns Observable type `<boolean>`
   */
  public postGroupUser(projectId: number, groupId: number, userId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${groupId}/users/${userId}`;
    return this.post<boolean>();
  }

  /**
   * Delete user from a group.
   *
   * @param  projectId Project ID
   * @param  groupId Group ID
   * @param  userId User ID
   * @returns Observable type `<boolean>`
   */
  public deleteGroupUser(projectId: number, groupId: number, userId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${groupId}/users/${userId}`;
    return this.delete<boolean>();
  }

  /**
   * Add group to a project.
   *
   * @param  projectId Project ID
   * @param  group new group
   * @returns Observable type `<boolean>`;
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public postGroup(projectId: number, group: any): Observable<boolean> {
    this.path = `/projects/${projectId}/groups`;
    return this.post<boolean>({ body: { groups_label: group.groups_label } });
  }

  /**
   * Group update.
   *
   * @param  projectId Project ID
   * @param  group Group object
   * @returns Observable type `<boolean>`
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateGroup(projectId: number, group: any): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${group.groups_id}`;
    return this.put<boolean>({ body: { groups_label: group.groups_label } });
  }

  /**
   * Rename user group from a project.
   *
   * @param  projectId Project ID
   * @param  groupId Group ID
   * @param  newName new group name
   * @returns Observable type `<boolean>`
   */
  public renameGroup(projectId: number, groupId: number, newName: string): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${groupId}/rename`;
    return this.post<boolean>({ body: { name: newName } });
  }

  /**
   * Delete user group from a project.
   *
   * @param  projectId Project ID
   * @param  groupId Group ID
   * @returns Observable type `<boolean>`
   */
  public deleteGroup(projectId: number, groupId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/groups/${groupId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsGroup instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsGroup` instance.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNewInstance(jsonObject: any): DsGroup {
    return new DsGroup(jsonObject);
  }
}
